import React, { useMemo, useState } from 'react';

import { StyleSheet } from 'react-native';

import { INutritionInfo } from '@rbi-ctg/menu';
import NutritionModal from 'components/nutrition';
import { Allergens } from 'components/nutrition/allergens';
import { AllergenGroup, NutritionFacts } from 'components/nutrition/types';
import { getAllergenGroups } from 'components/nutrition/utils';
import { Box, BoxProps, Typography } from 'components/ui';
import { IIngredients } from 'generated/sanity-graphql';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { spacing } from 'styles/constants';

import { Card } from './card';
import { IProductCard } from './types';

const Body = ({ style, ...props }: BoxProps) => {
  const isMobile = useIsMobileBreakpoint();

  return <Box {...props} style={[styles.body, !isMobile && styles.bodyDesktop, style]} />;
};

const CaloriesContainer = (props: React.ComponentPropsWithoutRef<typeof Typography>) =>
  React.createElement(Typography, {
    m: '$0',
    variant: 'copyTwo',
    textAlign: 'center',
    mb: '$4',
    ...props,
  });

const ProductCard: React.FC<React.PropsWithChildren<IProductCard>> = ({ product, size }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const enableAllergens = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS);
  const formatCalories = useFormatCalories();

  // We should always use the nutritionWithModifiers for the nutrition text
  const nutritionText = useMemo(() => formatCalories(product.nutritionWithModifiers.calories), [
    formatCalories,
    product.nutritionWithModifiers.calories,
  ]);

  let allergenGroups: AllergenGroup[] | undefined;
  if (enableAllergens && product.allergens) {
    allergenGroups = getAllergenGroups(product.allergens);
  }

  const nutritionFacts: NutritionFacts[] = [
    {
      itemName: product.name,
      itemImage: product.image,
      facts: product.nutritionWithModifiers || ({} as INutritionInfo),
      allergenGroups,
      ingredients: (product?.ingredients || []) as IIngredients[],
    },
  ];

  const nutritionInfoCard = (
    <Card key={product._id} image={product.image} size={size} onPress={() => setIsModalOpen(true)}>
      <Typography variant="headerTwo" accessibilityLevel={3} style={styles.title}>
        {product.name}
      </Typography>
      <Body>
        {nutritionText && <CaloriesContainer>{nutritionText}</CaloriesContainer>}
        {allergenGroups && <Allergens allergenGroups={allergenGroups} />}
      </Body>
    </Card>
  );

  return (
    <>
      {nutritionInfoCard}
      {isModalOpen && (
        <NutritionModal nutritionFacts={nutritionFacts} onDismiss={() => setIsModalOpen(false)} />
      )}
    </>
  );
};

export default React.memo(ProductCard);

const styles = StyleSheet.create({
  body: {
    flex: 1,
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$2,
  },
  bodyDesktop: {
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$3,
  },
  title: {
    letterSpacing: 1,
    margin: 0,
    textAlign: 'center',
    textTransform: 'none',
  },
});
